import { toast } from 'sonner'
import { isAllowedMimeType, validateFile } from '@/lib/schemas'
import {
  expandUploadFiles,
  isZipFile,
  type ArchiveSkipReason,
  type ExpandedArchive,
} from '@/lib/expand-upload-files'

/**
 * The words every upload surface uses to say what a drop or pick did: what it
 * added, which archives it opened, and — by name — what it left out and why.
 * A drop that silently loses files looks like it worked; a tender pack is
 * exactly where that costs someone a returnable schedule.
 */

// ===================
// Skip reasons
// ===================

export type UploadSkipReason = 'unsupported' | 'duplicate' | 'too-large' | ArchiveSkipReason

export interface SkippedUpload {
  name: string
  reason: UploadSkipReason
}

// Key order is display order.
export const SKIP_REASON_TEXT: Record<UploadSkipReason, string> = {
  unsupported: "this file type isn't supported",
  duplicate: 'already added',
  'too-large': 'too large to upload',
  'nested-archive': 'zip inside a zip',
  'archive-too-many-files': 'more than 300 files in one zip',
  'archive-too-large': 'zip unpacks to more than 500 MB',
  'archive-unreadable': "couldn't open this zip",
}

const MAX_SKIPPED_NAMES = 5

export function pluralFiles(count: number): string {
  return `${count} ${count === 1 ? 'file' : 'files'}`
}

export function describeSkipped(reason: UploadSkipReason, names: string[]): string {
  const shown = names.slice(0, MAX_SKIPPED_NAMES).join(', ')
  const hidden = names.length - MAX_SKIPPED_NAMES
  const more = hidden > 0 ? ` and ${hidden} more` : ''
  return `${pluralFiles(names.length)} skipped — ${SKIP_REASON_TEXT[reason]}: ${shown}${more}`
}

/** One line per reason, in `SKIP_REASON_TEXT` order. */
export function describeSkippedLines(skipped: SkippedUpload[]): string[] {
  return (Object.keys(SKIP_REASON_TEXT) as UploadSkipReason[]).flatMap((reason) => {
    const names = skipped.filter((f) => f.reason === reason).map((f) => f.name)
    return names.length > 0 ? [describeSkipped(reason, names)] : []
  })
}

/** Why `validateFile` refused a file, in the terms the user sees. */
export function invalidFileReason(file: File): 'unsupported' | 'too-large' {
  return isAllowedMimeType(file.type) ? 'too-large' : 'unsupported'
}

/**
 * The headline for a drop: "3 files added", or — when a ZIP was opened —
 * "Opened Tender Pack.zip — 23 files added".
 */
export function describeAdded(added: number, archives: ExpandedArchive[]): string | null {
  if (archives.length === 0) return added > 0 ? `${pluralFiles(added)} added` : null
  const names = archives.map((a) => a.name).join(', ')
  return added > 0
    ? `Opened ${names} — ${pluralFiles(added)} added`
    : `Opened ${names} — no files added`
}

export function describeOpening(files: File[]): string | null {
  const archives = files.filter((file) => isZipFile(file)).map((file) => file.name)
  return archives.length > 0 ? `Opening ${archives.join(', ')}…` : null
}

// ===================
// Toast surfaces
// ===================

/**
 * For upload surfaces that report through a toast (the bid Documents tab and
 * the Library). Opens any ZIPs, drops system files, applies `validateFile`, and
 * says what happened. Returns the files to upload. Duplicate handling stays with
 * each surface — its rules differ (warn-and-override vs server-side check).
 *
 * Quiet when nothing was opened or skipped: a plain drop of good files already
 * shows up as upload progress.
 */
export async function prepareUploadsWithFeedback(input: File[]): Promise<File[]> {
  const opening = describeOpening(input)
  const toastId = opening ? toast.loading(opening) : undefined

  const expanded = await expandUploadFiles(input)
  const skipped: SkippedUpload[] = [...expanded.skipped]
  const valid: File[] = []
  for (const file of expanded.files) {
    if (validateFile(file).valid) {
      valid.push(file)
    } else {
      skipped.push({ name: file.name, reason: invalidFileReason(file) })
    }
  }

  const headline =
    expanded.expandedArchives.length > 0 || skipped.length > 0
      ? describeAdded(valid.length, expanded.expandedArchives) ?? 'No files added'
      : null
  const skippedLines = describeSkippedLines(skipped)
  const toastOptions = {
    description:
      skippedLines.length > 0 ? (
        <div className="space-y-1">
          {skippedLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : undefined,
    ...(toastId !== undefined ? { id: toastId } : {}),
  }

  if (headline === null) {
    if (toastId !== undefined) toast.dismiss(toastId)
  } else if (valid.length === 0) {
    toast.error(headline, toastOptions)
  } else if (skipped.length > 0) {
    toast.warning(headline, toastOptions)
  } else {
    toast.success(headline, toastOptions)
  }

  return valid
}
